import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@attendance/ui/utils';
import { PageHeader, type PageHeaderProps } from './PageHeader.js';
import { HeaderExportSlot, headerExportHost, useHeaderExportSlot } from './HeaderExport.js';
import { isWideRoute } from '../layout/navItems.js';

/* ===========================================================================
 * PageShell — the page's anatomy, so a screen declares WHAT is in each band and
 * never where it sits.
 *
 * §2 of docs/design-rules.md opens every admin screen with the same four things
 * in the same order — an uppercase crumb line, a 34px page title, THEN THE ONE
 * TOOLBAR, then content — and says the toolbar sticks: "Any page-level sticky
 * element sits at `top: 56px`, never `top: 0`". It casts NO scroll shadow:
 * the bar's own resting shadow is the only one (owner, 2026-11-02).
 *
 * When this was written, 50 screens rendered `PageHeader`, 46 rendered
 * `ControlBar`, nine files mentioned `sticky` at all — every one of them
 * hand-rolled — and there was no shared page shell. Every screen re-assembled
 * the three bands itself, which is why the sticky toolbar existed on roughly one
 * of them and the other forty-five scrolled their filters off the top. A rule
 * that lives in a document and in one screen is not a rule; it is a precedent
 * nobody can find. This is where it lives now.
 *
 * ---------------------------------------------------------------------------
 * THE THREE BANDS, and what each is allowed to decide
 *
 *   header band    delegates ENTIRELY to `PageHeader`. Not reimplemented, not
 *                  wrapped in anything that changes it: the eyebrow rules
 *                  (3.1/3.2), the wrapping action cluster and the never-squeezed
 *                  title are that component's contract and are tested there.
 *
 *   toolbar band   `sticky top-header` on a solid ground whose bottom edge
 *                  is the bar's; no shadow and no change on scroll.
 *                  The band is shell geometry; what goes IN it is the screen's.
 *
 *   content band   the vertical rhythm, and — on a wide route only — the gutter.
 *
 * ---------------------------------------------------------------------------
 * SIX DECISIONS, EACH OF WHICH COULD HAVE GONE THE OTHER WAY
 *
 * 1. THE HEADER PROPS ARE FLATTENED, NOT A SLOT. `PageShellProps` extends
 *    `PageHeaderProps`, so adopting the shell on a screen is: rename the
 *    `<PageHeader` tag, move the toolbar into a prop, close the tag around the
 *    body. Fifty screens have to move; a slot (`header={<PageHeader …/>}`) would
 *    have been a wider diff on every one of them AND would have let a screen put
 *    something that is not a page header in the header band. Flattening is also
 *    what makes the delegation total: a prop added to `PageHeader` tomorrow is a
 *    `PageShell` prop tomorrow, with nothing here to update.
 *
 * 2. THE TOOLBAR SLOT TAKES A NODE, NOT `ControlBar`'s SLOTS. Re-declaring
 *    search / filters / segmented / sort / tools / status / actions here would
 *    put a second copy of the one toolbar's fixed reading order in a second
 *    file, and two copies of a contract are a contract that drifts. `ControlBar`
 *    owns that order and keeps owning it; this owns where the bar SITS. It also
 *    means a screen whose toolbar is legitimately not a `ControlBar` — the
 *    attendance scope bar, Operations' view switcher plus its portal host — gets
 *    the same sticky treatment instead of being told it does not qualify.
 *
 * 3. NO TOOLBAR MEANS NO BAND. An absent (or `false` / `null` / `''`) toolbar
 *    renders no sticky element at all — not an empty gradient strip with 20px of
 *    padding in it. Same `filled()` semantics `ControlBar` uses on its own slots,
 *    and for the same reason: call sites reach for every value React renders as
 *    nothing (`{canFilter && <ControlBar …/>}` yields `false`).
 *
 * 4. WIDENESS IS READ FROM THE ROUTE, NOT PASSED IN. `AppShell` exempts
 *    `isWideRoute()` paths from the `max-w-content` cap and gives them no gutter,
 *    because "a map capped at the text measure is not a control room". If this
 *    component took a `wide` prop, a screen could disagree with the shell about
 *    its own route and the failure would be a page with either two gutters or
 *    none. Both ask `navItems.ts` the same question instead. `WIDE_ROUTES` stays
 *    the single declaration.
 *
 * 5. THERE IS NO SCROLLED STATE. The band used to read `usePageScrolled()`
 *    and grow a shadow past 12px; that drew a second line across the content
 *    under a bar that already has its own (owner, 2026-10-31, 2026-11-02).
 *    The band looks the same at rest and scrolled, and listens to nothing.
 *
 * 6. NO HORIZONTAL BLEED. The handoff's band carries the page gutter itself
 *    (`padding: 6px 34px 14px`) because in the mock nothing above it does. Here
 *    the shell's content wrapper already applies `p-gutter`, and the content
 *    that scrolls under this band is inside that same wrapper — so a
 *    `-mx-gutter` bleed would widen the gradient over a region no content can
 *    occupy, and would push a wide route's band off the edge of the screen,
 *    since a wide route gets no wrapper padding to cancel. The band takes the
 *    block padding and inherits the inline.
 *
 * ---------------------------------------------------------------------------
 * A SECOND STICKY ELEMENT BELOW THE TOOLBAR
 *
 * Some screens need one — a bulk-action bar that appears with a selection, a
 * sub-header over a long grid. It cannot be `top-header`, or it lands underneath
 * the toolbar; and it cannot be `top-[104px]`, because §6 of the rules file bans
 * exactly that ("Hardcode padding for chrome whose height depends on content —
 * measure it") and this band's height depends on whether the bar wrapped.
 *
 * So the band is measured, and its height is published on the shell root as
 * `--page-toolbar-h`. A screen composes with the exported class:
 *
 *     <div className={cn(stickyBelowToolbar, 'z-20 …')}>…</div>
 *
 * A slot was the alternative and is worse: the second sticky is conditional on
 * nearly every screen that wants one, it belongs in the content flow (it is
 * usually attached to a table), and a slot would have to guess its z-index and
 * its margins. A measured offset lets it stay where it is.
 * ======================================================================== */

/**
 * `top` for a page-level sticky element that must park UNDER the toolbar band.
 *
 * 56px of app bar plus the measured height of the band, falling back to 0 when
 * there is no band (or before the first measurement, which is one frame at
 * most) — so the same class is correct on a screen with a toolbar and on one
 * without. Reads the tokens, never a literal.
 */
export const stickyBelowToolbar =
  'sticky top-[calc(var(--header-height)+var(--page-toolbar-h,0px))]';

/** Is there anything in this slot worth a band? See the helper in `ControlBar`. */
function filled(slot: ReactNode): boolean {
  return slot != null && slot !== false && slot !== '';
}

export interface PageShellProps extends Omit<PageHeaderProps, 'className'> {
  /**
   * The one toolbar. Usually a `<ControlBar>`; anything that belongs in the
   * band is accepted. Omit it and the band is not rendered at all.
   *
   * Two nodes stack: the band is a column, so a screen whose bar comes in two
   * pieces (a view switcher over a filter row) passes a fragment and both stick
   * together as one object.
   */
  toolbar?: ReactNode;
  /**
   * Render the header and toolbar bands at all. `false` is for a genuine
   * chrome-less mode — Operations' wall view, where the whole point is that the
   * map is the screen — and is not a way to opt out of the anatomy.
   */
  chrome?: boolean;
  /**
   * The page's MEASURE, and it is a closed set rather than a class.
   *
   * ---------------------------------------------------------------------------
   * THE RULE: THE MEASURE BELONGS TO THE COLUMN INSIDE THE PAGE, NOT TO THE PAGE.
   *
   *   `content`  (the default) the page takes the full 1600px and LAYS OUT into
   *              it — a table, a board, a map, a record beside a rail of state,
   *              a queue beside its histogram, two columns of notice cards.
   *              Every reading column INSIDE it still keeps a measure: past
   *              ~720px the eye loses the start of the next line, and that fact
   *              did not change. It is enforced one level in, by the column, at
   *              the width the column actually has.
   *
   *   `form`     the page IS one reading column all the way down, with nothing
   *              that legitimately belongs beside it: a card of three password
   *              fields, a three-card import wizard asking one question, a 404.
   *              There is no inside to cap, so the page caps itself.
   *
   * The test, and it is a layout question rather than a content one: IS THERE A
   * SECOND COLUMN? Not "could one be invented" — is there something the page
   * already holds that a reader would rather have beside the body than under
   * it? A record has its state panels. A form has what it is about to produce,
   * or the list of what it produced before. A stream of short cards has more of
   * itself. A password change has nothing, and stays `form`.
   *
   * WHAT THIS REPLACES, AND WHY. The rule read "THE BODY DECIDES, NOT THE
   * AUDIENCE": prose and cards of fields took `form`, data you scan took
   * `content`. It fixed something real — the audience rule before it shipped
   * /corrections at 1600px and /leave at 720px for one person's list of their
   * own requests — and the half of it about LINE LENGTH is kept above, because
   * it is simply true.
   *
   * What it got wrong is WHICH BOX the 720 goes on. Putting it on the page
   * bought the measure by giving back two thirds of the window, permanently and
   * invisibly: /profile's own file argued itself out of the mock's two-column
   * record/state split ON THE GROUND THAT THE PAGE WAS 720px WIDE — a cap
   * deciding a layout, which is backwards. The owner rejected the result twice,
   * naming /announcements, /letters and /profile, and the complaint is correct:
   * those screens had the measure AND the dead space, and one of them, the
   * worker's own record, disagreed about its own shape with the admin's copy of
   * the same person sitting at full width two clicks away.
   *
   * WIDER IS NOT THE SAME AS STRETCHED, and that is the whole discipline here. A
   * 1600px text input, or a paragraph set across 1600px, is worse than 720px,
   * not better. A page that takes the width owes a layout for it — see
   * `Hadir Employee.dc.html`'s 1.55fr/1fr record-and-rail, which §4a of
   * docs/design-system-migration.md also prescribes for /corrections in exactly
   * these words: "the body is capped at a 720px reading column that has to go".
   * A page that cannot name its second column is a page that should still say
   * `form`.
   *
   * A KPI ROW IS NOT A REASON EITHER WAY. `StatRow` re-columns on the TILE's
   * width, so four 170px tiles are as happy in 720px as in 1600px; a tile row
   * has never been an argument about the page's measure.
   *
   * ---------------------------------------------------------------------------
   * `AppShell` hands every route `max-w-content` (1600px), and that is now the
   * answer for all but a handful of screens. `routes/pageWidth.test.tsx` pins
   * both halves of the rule: where the declaration goes when a page opts down —
   * the width has to be on the page's OUTERMOST element, because that is the
   * box `AppShell`'s wrapper is capping — and that a page which does NOT opt
   * down declares columns, so taking the width and stretching one column across
   * it fails a test rather than shipping.
   *
   * `className` cannot do it — that is the CONTENT band — so five screens kept a
   * `<div className="mx-auto w-full max-w-form">` wrapper outside the shell
   * purely to satisfy the rule. That wrapper is not free: it is a second box
   * around a component whose whole job is to be the page's box, and it puts the
   * sticky band inside the reading column by accident rather than by decision.
   *
   * TWO VALUES, NOT A `rootClassName`. A free-form class on the root would let a
   * screen invent a fourth measure (this app has already shipped `max-w-3xl` and
   * `max-w-md` as private guesses at 720px), and — more to the point — the shell
   * would not KNOW which layout it is in. With a closed set it does: the band's
   * gradient spans exactly the column the content occupies, because the same
   * element caps both. `content` is the default and adds nothing, so `AppShell`
   * stays the one place the 1600px cap is declared.
   *
   * Not for a wide route: `isWideRoute()` pages are full-bleed control rooms and
   * `max-w-form` on one would cap the map at a reading column.
   */
  width?: 'content' | 'form';
  /** Extra classes for the header band. Forwarded to `PageHeader`. */
  headerClassName?: string;
  /**
   * Extra classes for the CONTENT band. The default rhythm is the mock's 16px
   * column (`gap-component`); a screen that wants the 24px section step passes
   * `gap-section` here rather than putting margins back on its children.
   */
  className?: string;
}

/**
 * THE BODY GUTTER A WIDE ROUTE HAS TO SUPPLY ITSELF.
 *
 * `AppShell` gives an `isWideRoute()` page no padding at all — a control
 * room's map is meant to run to the glass — and this component puts the gutter
 * back on the head and toolbar bands but deliberately not on the body: below
 * the toolbar, a wide screen owns its own edges.
 *
 * A wide page that is NOT full-bleed therefore has to pass this as its
 * `className`, or its content sits 34px outside the title above it. Exported
 * because two screens were keeping the same string in step by hand — the
 * attendance month and day views — and the day one had already fallen behind.
 */
export const WIDE_BODY_GUTTER = 'px-gutter-sm pb-gutter-sm md:px-gutter md:pb-gutter';

export const PageShell = forwardRef<HTMLDivElement, PageShellProps & { children?: ReactNode }>(
  function PageShell(
    {
      toolbar,
      chrome = true,
      width = 'content',
      headerClassName,
      className,
      children,
      ...header
    },
    ref
  ) {
    const { pathname } = useLocation();
    const wide = isWideRoute(pathname);
    const hasToolbar = filled(toolbar);
    // A list's export portals into the header's actions (`HeaderExport`, §2).
    const exportSlot = useHeaderExportSlot();
    const actions = headerExportHost(exportSlot.claimed, exportSlot.setHost, header.actions);

    /* --- the measured band height ------------------------------------------
     * Written as a custom property on the ROOT rather than returned as a
     * number, so the second sticky element can read it from CSS without this
     * component knowing that it exists. `offsetHeight` and not the observer's
     * `contentRect`: the band's block padding is part of the offset a sibling
     * has to clear, and `contentRect` excludes it.
     *
     * Guarded like the IntersectionObserver above it — jsdom has no
     * ResizeObserver either, and the fallback in `stickyBelowToolbar` (0px)
     * degrades to "sticks at the app bar", which is where it would have been
     * without any of this.
     * -------------------------------------------------------------------- */
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [bandEl, setBandEl] = useState<HTMLDivElement | null>(null);

    const setRoot = useCallback(
      (node: HTMLDivElement | null) => {
        rootRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref]
    );

    useEffect(() => {
      const root = rootRef.current;
      if (!root) return;
      if (!bandEl) {
        root.style.removeProperty('--page-toolbar-h');
        return;
      }
      const write = (): void =>
        root.style.setProperty('--page-toolbar-h', `${bandEl.offsetHeight}px`);
      write();
      if (typeof ResizeObserver === 'undefined') return;
      const observer = new ResizeObserver(write);
      observer.observe(bandEl);
      return () => observer.disconnect();
    }, [bandEl]);

    return (
      <HeaderExportSlot.Provider value={exportSlot.value}>
      <div
        ref={setRoot}
        data-page-shell=""
        className={cn(
          // A WIDE ROUTE IS A FLEX COLUMN THAT FILLS THE SHELL, because that is
          // the only shape in which a control room's map can take the leftover
          // height. `AppShell` gives these routes `h-full w-full` and no
          // padding; everything the gutter used to do for them is done here.
          //
          // `bg-background` is redundant against the shell's own column and is
          // NOT redundant in full screen: `requestFullscreen()` is called on
          // this element, and a fullscreened element with a transparent ground
          // is composited over the UA's black backdrop.
          wide && 'flex h-full min-h-0 w-full flex-1 flex-col bg-background',
          // THE WHOLE-PAGE READING COLUMN, ON THE ROOT — which is the only
          // element that can carry it. `AppShell`'s wrapper caps the page at
          // `max-w-content`, so a narrower measure has to be declared on the box
          // that wrapper holds (`routes/pageWidth.test.tsx` asserts exactly
          // that), and it takes the sticky band with it rather than leaving a
          // 1600px gradient over a 720px column. Only for a page that is one
          // column all the way down; a page with a second column caps the
          // columns instead and leaves this alone.
          width === 'form' && 'mx-auto w-full max-w-form'
        )}
      >
        {chrome && (
          <div
            className={cn(
              // On a normal route the shell's wrapper has already applied the
              // gutter and the reading cap, so the bands sit in the flow with
              // nothing of their own. On a wide route there is no wrapper
              // padding, so this head band takes the gutter (and the body
              // below it does not — a map is full-bleed by definition).
              //
              // THE INLINE GUTTER AND THE BLOCK ONE ARE NOT THE SAME NUMBER.
              // `--gutter` (34px) is the distance from the rail, and the head
              // band keeps it on the inline axis. Above the eyebrow the handoff
              // sets 26px, not 34 (`padding: 26px 34px 20px`, Hadir
              // Operations.dc.html:196) — the band already has the shell bar
              // directly above it, so the full gutter there reads as a gap
              // rather than as a margin. The 20px below is PageHeader's `mb-5`.
              // No token lands on 26px; `--gutter` is the only page-level
              // spacing role and it is the value being deliberately departed
              // from, so this one is written out.
              wide && 'shrink-0 px-gutter-sm pt-gutter-sm md:px-gutter md:pt-[26px]'
            )}
          >
            <PageHeader {...header} actions={actions} className={headerClassName} />
          </div>
        )}

        {/* THE STICKY TOOLBAR AND WHAT SCROLLS UNDER IT SHARE ONE PARENT.
            They did not, before: the toolbar sat as the LAST child of the
            head band above (PageHeader + toolbar, nothing after
            it), and `children` was a separate box below that. `position:
            sticky` stays pinned only within the bounds of its OWN containing
            block — not the page's scrollport — so the toolbar's containing
            block ended at almost exactly the point stickiness would have
            begun, and it was pushed off with the head band the instant a
            reader scrolled past it. It never had anywhere to stay stuck.
            Nesting both under this one box gives the toolbar the whole
            remaining page height as its containing block, which is what lets
            it actually stay pinned while `children` scrolls beneath it. */}
        <div className={cn('flex flex-col', wide && 'min-h-0 flex-1')}>
          {chrome && hasToolbar && (
            <div
              ref={setBandEl}
              data-page-toolbar=""
              className={cn(
                /* THE GUTTER MOVES HERE WITH THE TOOLBAR: its parent is shared
                   with the full-bleed content, so the toolbar declares its own. */
                wide && 'px-gutter-sm md:px-gutter',
                /* STICKY AT THE HEADER OFFSET, NEVER AT ZERO. The shell's
                   content column is the app's single scrollport and the 56px
                   bar is genuinely sticky inside it, so `top-0` here would
                   park this band UNDERNEATH the bar rather than below it.
                   `top-header` reads `--header-height`, which is the same
                   token the bar's own `min-h-header` reads — the two cannot
                   drift apart. (§2 of docs/design-rules.md.) */
                'sticky top-header z-30',
                /* 6px above the bar and NOTHING below it: the band's bottom
                   edge is the bar's own (owner, 2026-11-02: "bottom line still
                   showing down??"). The 14px to the content is the content
                   band's own top padding, so it scrolls away with the page
                   instead of being an opaque strip laid across the grid's
                   header row. */
                'flex flex-col gap-element pt-1.5',
                /* A SOLID GROUND, AND NO SCROLLED STATE. The band used to grow
                   a `shadow-sticky` and turn its gradient tail opaque once the
                   page scrolled — a second line under a bar that already has
                   its own resting shadow (owner, 2026-10-31 and 2026-11-02).
                   The bar casts nothing extra now; the ground only covers the
                   6px above it, so content never shows between it and the app
                   bar. Nothing here changes on scroll at all. */
                'bg-background'
              )}
            >
              {toolbar}
            </div>
          )}

          <div
            className={cn(
              // The 14px under the bar, which the band no longer carries.
              'flex flex-col',
              chrome && hasToolbar && 'pt-3.5',
              // The mock's content band: a 16px column. A screen that wants the
              // section step says so; a screen that puts margins back on its
              // children is fighting the shell.
              'gap-component',
              // A wide route's body is the leftover height, and `min-h-0` is what
              // lets a scroller inside it (the site rail, a 2,000-row table)
              // scroll instead of stretching the column past the viewport.
              wide && 'min-h-0 flex-1',
              className
            )}
          >
            {children}
          </div>
        </div>
      </div>
      </HeaderExportSlot.Provider>
    );
  }
);
